import { useEffect, useState } from 'react'
import { Layout, Card, Row, Col, Statistic, Typography, message, Table, Button, Popconfirm, Modal, Form, Input, Space, Tag } from 'antd'
import {
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  EditOutlined,
  LockOutlined,
} from '@ant-design/icons'
import { adminApi } from '../api/admin'
import { UserInfo } from '../types'
import { useUserStore } from '../store/user'
import { useNavigate } from 'react-router-dom'

const { Title } = Typography
const { Header, Content } = Layout

export default function AdminDashboard() {
  const [users, setUsers] = useState<UserInfo[]>([])
  const [stats, setStats] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null)
  const [form] = Form.useForm()
  const { userInfo } = useUserStore()
  const navigate = useNavigate()

  // 检查是否是管理员
  useEffect(() => {
    if (!userInfo || !userInfo.isAdmin) {
      message.error('您没有管理员权限')
      navigate('/')
    }
  }, [userInfo])

  const loadData = async () => {
    setLoading(true)
    try {
      const [userList, systemStats] = await Promise.all([
        adminApi.getAllUsers(),
        adminApi.getSystemStats(),
      ])
      setUsers(userList)
      setStats(systemStats)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleResetPassword = async () => {
    if (!selectedUser) return
    const values = await form.validateFields()
    try {
      await adminApi.resetUserPassword(selectedUser.id, values.newPassword)
      message.success('密码重置成功')
      setPasswordModalVisible(false)
      form.resetFields()
    } catch (error) {
      // 错误已在 request 中处理
    }
  }

  const handleToggleStatus = async (user: UserInfo, isActive: boolean) => {
    try {
      await adminApi.toggleUserStatus(user.id, isActive)
      message.success(isActive ? '已启用用户' : '已禁用用户')
      loadData()
    } catch (error) {
      // 错误已在 request 中处理
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
    },
    {
      title: '角色',
      key: 'role',
      render: (_: any, user: UserInfo) => (
        <Tag color={user.isAdmin ? 'blue' : 'default'}>
          {user.isAdmin ? '管理员' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '状态',
      key: 'status',
      render: (_: any, user: UserInfo) => (
        <Tag color={user.isActive ? 'green' : 'red'}>
          {user.isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, user: UserInfo) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<LockOutlined />}
            onClick={() => {
              setSelectedUser(user)
              setPasswordModalVisible(true)
            }}
          >
            重置密码
          </Button>
          {user.isActive ? (
            <Popconfirm
              title="确定禁用该用户吗？"
              onConfirm={() => handleToggleStatus(user, false)}
            >
              <Button type="link" danger size="small">
                禁用
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="确定启用该用户吗？"
              onConfirm={() => handleToggleStatus(user, true)}
            >
              <Button type="link" size="small">
                启用
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <Layout className="min-h-screen bg-gray-50">
      <Header className="bg-white border-b shadow-sm flex items-center px-6">
        <Title level={4} style={{ margin: 0 }}>管理后台</Title>
      </Header>
      <Content className="p-6">
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card bordered={false} className="shadow-sm">
              <Statistic
                title="用户总数"
                value={stats.userCount || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false} className="shadow-sm">
              <Statistic
                title="小组数量"
                value={stats.teamCount || 0}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false} className="shadow-sm">
              <Statistic
                title="碎片总数"
                value={stats.fragmentCount || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false} className="shadow-sm">
              <Statistic
                title="活跃用户"
                value={stats.activeUserCount || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        <Card
          bordered={false}
          className="shadow-sm"
          title="用户管理"
        >
          <Table
            dataSource={users}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </Card>
      </Content>

      <Modal
        title="重置用户密码"
        open={passwordModalVisible}
        onOk={handleResetPassword}
        onCancel={() => setPasswordModalVisible(false)}
        destroyOnClose
      >
        <p>正在重置用户 <b>{selectedUser?.nickname}</b> 的密码</p>
        <Form form={form} layout="vertical">
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, max: 20, message: '密码长度为6-20位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}
